"use client"

import styled from "@emotion/styled"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { Fragment } from "react"
import { colors } from "./theme/colors"
import { breakpoints, layoutPadding } from "./theme/mediaQueries"
import { fontFamily, fontWeights, typographyVariants } from "./theme/typography"

// EGDE-261: breadcrumbs use the semantic Body 2 typography token (primary
// font family, weight 400, body2 size/line-height) with 0.15% letter spacing
// instead of a hardcoded font-family string.
const body2 = typographyVariants.body2
const breadcrumbLetterSpacing = "0.0015em" // 0.15%

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  label: string
  href: string
}

export interface BreadcrumbsProps {
  ancestors: BreadcrumbItem[]
  current: string
  /**
   * Public origin (process.env.ORIGIN). When provided, breadcrumb item URLs in
   * the emitted `BreadcrumbList` JSON-LD are absolute — as Google recommends.
   */
  baseUrl?: string
}

// ---------------------------------------------------------------------------
// Structured data (BreadcrumbList)
// ---------------------------------------------------------------------------

/**
 * Build the schema.org BreadcrumbList for the current trail. The default-locale
 * `/he` prefix is stripped so item URLs match the pages' canonical form.
 */
function buildBreadcrumbJsonLd(
  ancestors: BreadcrumbItem[],
  current: string,
  baseUrl?: string,
) {
  const base = baseUrl?.replace(/\/$/, "") ?? ""
  const toUrl = (href: string) => {
    const clean = href.replace(/^\/he(?=\/|$)/, "") || "/"
    return `${base}${clean}`
  }

  const itemListElement = [
    ...ancestors.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: a.label,
      item: toUrl(a.href),
    })),
    {
      "@type": "ListItem",
      position: ancestors.length + 1,
      name: current,
    },
  ]

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  }
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Root = styled.nav`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 12px ${layoutPadding.mobile}px;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: ${breakpoints.lg}px;
    padding: 16px ${layoutPadding.desktop}px;
    margin: 0 auto;
  }

  @media (min-width: ${breakpoints.xl}px) {
    max-width: ${breakpoints.xl}px;
    padding: 16px ${layoutPadding.desktopL}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    max-width: ${breakpoints.xxl}px;
    padding: 16px ${layoutPadding.desktopXl}px;
  }
`

const Trail = styled.ol`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  list-style: none;
  margin: 0;
  padding: 0;
`

const Separator = styled.li`
  font-family: ${fontFamily.primary};
  font-weight: ${fontWeights.medium};
  font-size: ${body2.mobile.fontSize}px;
  line-height: ${body2.mobile.lineHeight}px;
  letter-spacing: ${breadcrumbLetterSpacing};
  color: ${colors.primitives.neutral500};
  flex-shrink: 0;
  user-select: none;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: ${body2.desktop.fontSize}px;
    line-height: ${body2.desktop.lineHeight}px;
  }
`

const AncestorLink = styled(Link)`
  font-family: ${fontFamily.primary};
  font-weight: ${fontWeights.medium};
  font-size: ${body2.mobile.fontSize}px;
  line-height: ${body2.mobile.lineHeight}px;
  letter-spacing: ${breadcrumbLetterSpacing};
  color: ${colors.primitives.neutral500};
  text-decoration: none;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: ${body2.desktop.fontSize}px;
    line-height: ${body2.desktop.lineHeight}px;
    padding-bottom: 6px;
  }

  &:hover {
    text-decoration: underline;
  }
`

const CurrentPage = styled.span`
  font-family: ${fontFamily.primary};
  font-weight: ${fontWeights.medium};
  font-size: ${body2.mobile.fontSize}px;
  line-height: ${body2.mobile.lineHeight}px;
  letter-spacing: ${breadcrumbLetterSpacing};
  color: ${colors.primitives.neutral600};

  @media (min-width: ${breakpoints.lg}px) {
    font-size: ${body2.desktop.fontSize}px;
    line-height: ${body2.desktop.lineHeight}px;
    padding-bottom: 6px;
  }
`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Breadcrumbs({
  ancestors,
  current,
  baseUrl,
}: BreadcrumbsProps) {
  // EGDE-634: the landmark label was hardcoded Hebrew, so a screen reader on
  // /en announced the trail in Hebrew. Invisible to a sighted tester, same bug.
  const t = useTranslations("InnerPages")
  const jsonLd = buildBreadcrumbJsonLd(ancestors, current, baseUrl)
  return (
    <Root aria-label={t("breadcrumbsLabel")}>
      <script
        type="application/ld+json"
        // Escape `<` so a CMS-authored label containing "</script>" can't break
        // out of the tag; content is otherwise our own trail data.
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
        }}
      />
      <Trail>
        {ancestors.map((ancestor, i) => (
          <Fragment key={ancestor.href}>
            <li>
              <AncestorLink href={ancestor.href}>{ancestor.label}</AncestorLink>
            </li>
            <Separator role="presentation" aria-hidden="true">
              /
            </Separator>
          </Fragment>
        ))}
        <li>
          <CurrentPage aria-current="page">{current}</CurrentPage>
        </li>
      </Trail>
    </Root>
  )
}

export { Breadcrumbs }
